/*
 * assets/bag.js — everything that moves or answers a tap INSIDE the bag (Round 6, storefront #49).
 *
 * The bag's markup is sections/cart-drawer.liquid (+ snippets bag-top, bag-totals, cart-item, bag-complete-the-set,
 * bag-care-set).
 * Opening, closing, Back, focus and every cart write are assets/cart.js (<cart-items>); this file listens to its
 * events and calls its API:
 *   sofsy:bag-opened   {slideMs, pending}           the run starts when the slide ends; Secure checkout is watched
 *                                                    (bag.checkout.impression, zao#617); an open sent before this
 *                                                    file loaded is picked up from cart-items.lastOpen (catchUpOpen)
 *   sofsy:bag-add-settled {ok}                       the add that opened the bag at once has been answered (zao#617)
 *   sofsy:bag-rendered {prev, next, prevBody, prevFigures, open}  after any cart write: the run goes old -> new, Undo rows and
 *                                                    focus are put back, Complete the Set and The Care Set
 *                                                    leave / fold
 *   sofsy:bag-closed   {reason, silent}             bag_closed {how, ms_open, checkout_seen} / bag_scrolled (not
 *                                                    when silent), Undo rows end
 *   sofsy:bag-escape   (cancelable)                 Escape closes an open row or question first
 *
 * B02 F0, the delivery run (G2C, Ben: "we want this animation for the line"), ported from the Round 6 studio
 * (lib5_bag_top.js bt5_prog, lib6_bag_top.js bt6_with / bt6_fix) at the approved values:
 *   - every open: the 7 px dot and the fill run from the start of the line to the bag's total in 1.2 s on
 *     cubic-bezier(.25,1,.5,1) (E2), starting when the slide ends; the dot's soft halo (0 0 0 3px rgba(0,0,0,.1))
 *     holds for 0-80 % of the run and fades by 100 %; then '$110' fades in under the dot (250 ms ease-out, 2 px rise);
 *   - reached: the dot runs into the van and fades (150 ms); the van goes .30 -> 1 (200 ms linear);
 *   - a change in the open bag runs old -> new on the same curve and duration; going back below the threshold the
 *     dot fades in (150 ms) and the van goes 1 -> .30 (200 ms); the words cross-fade (150 ms out, 150 ms in) when
 *     they change;
 *   - it plays once per open, never loops; reduced motion, or the theme setting off, shows the end state.
 *   Dot and fill move by transform (translateX / scaleX), per the spec task.
 *
 * B04 F0, the line (O1, C24, P30, MW, LP0, SN + Ben's diagonal and slow message):
 *   - colour / size open their choices inside the line (200 ms, the line grows; one row open at a time); one tap
 *     applies and closes; a tap on the same row, outside, or Escape closes with no change;
 *   - a change is cart.js swapLine (add the new, then take the old away: never loses an item); a refusal leaves the
 *     bag as it was and the row says "<shade>: sold out in <size>";
 *   - a sold-out shade (last, with its diagonal) changes nothing: the row reads "<shade>: sold out in <size>" for long
 *     enough to read (Ben): 350 ms in, 3 s, 400 ms out;
 *   - the stepper writes by line key (cart.js writeLines), capped at min(15, stock); at one pair − does nothing;
 *   - X asks "Remove Cherry Red, XS-S?" (Remove / Keep); Remove takes the line away at once and leaves
 *     "Removed: … Undo" for 5 s; Undo adds the same variant and quantity back (cart.js addItems, source 'undo');
 *   - a changed or undone line stays where she left it (orderLines: the bag remembers the order it showed);
 *   - the photo and the name do nothing (LP0); the tap is counted.
 * B10 F0: + adds that piece, the link adds every card (cart.js addItems, sources 'bag_cts' / 'bag_cts_link');
 *   the card fades (200 ms) and the rest slide left (240 ms); the block folds (240 ms) when the set is complete.
 * #63 A16, The Care Set (the last block in the bag): the same cards, + and fold, its own hooks (data-bag-care*), the
 *   link "Complete the Care Set" adding every card in one request (sources 'bag_care' / 'bag_care_link'); it counts
 *   bag_care_set_view on every open that shows it and bag_care_set_add on every add that worked.
 * B02 SC1 (setting bag_returning_header, off at launch): the header slides back over the lines after a 10 px scroll up.
 *
 * Tracking goes through window.sofsyTrack (assets/sofsy-track.js, step 8: the event dictionary's names and sinks).
 * zao#617, did she see Secure checkout: bag.checkout.impression {ms_since_open, scrolled} once per open, the first
 * time at least half of the #checkout button is inside the viewport (IntersectionObserver; ms since the open, and
 * whether she had scrolled the bag by then); bag_closed adds ms_open (open to close) and checkout_seen (that
 * impression happened in this open; left out where IntersectionObserver is missing). While the add that opened the
 * bag is still pending (cart.js beginPendingAdd), complete_set_viewed and bag_care_set_view wait for its answer, so
 * they describe the bag as the add left it, as before; the delivery run holds at its start and runs once when the
 * answer comes. When Shopify refused that add outright (sofsy:bag-add-settled ok:false, cartChanged not true), this
 * open's complete_set_viewed, bag_care_set_view and bag_closed carry add_failed:true, so they never count as real
 * bag views. The refusal's sentence ([data-bag-status], say(text, {stay:true})) stays until her next tap in the bag
 * or the close.
 * view_cart, bag_items_added, shipping_threshold_crossed and checkout_clicked come from sofsy-track.js itself, from
 * cart.js's events. No fetch of its own, no gtag / fbq / dataLayer.
 */
(() => {
  if (window.SofsyBag) return;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- the approved timings (sofsy-ds.css tokens, with the same values as fallbacks) ---------- */
  const token = (name, fallback) => {
    try {
      const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      if (!raw) return fallback;
      if (raw.endsWith('ms')) return parseFloat(raw);
      if (raw.endsWith('s')) return parseFloat(raw) * 1000;
      return fallback;
    } catch (_) {
      return fallback;
    }
  };
  const tokenText = (name, fallback) => {
    try {
      return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
    } catch (_) {
      return fallback;
    }
  };
  const T = {
    run: () => token('--ds-dur-run', 1200),
    ease: () => tokenText('--ds-ease-run', 'cubic-bezier(0.25, 1, 0.5, 1)'),
    label: 250,
    dotOut: 150,
    van: 200,
    words: () => token('--ds-dur-fade', 150),
    noteIn: () => token('--ds-dur-message-in', 350),
    noteHold: () => token('--ds-dur-message-hold', 3000),
    noteOut: () => token('--ds-dur-message-out', 400),
    panel: 200,
    undo: 5000,
    collapse: 200,
    ctsFade: 200,
    ctsSlide: 240,
    ctsFold: 240
  };
  // cart.js close reasons -> the event dictionary's `how` values for bag_closed (the rest are the same words).
  const CLOSE_HOW = Object.freeze({ x: 'close', dim: 'scrim' });
  const HALO = '0 0 0 3px rgba(0, 0, 0, 0.1)';
  const NO_HALO = '0 0 0 3px rgba(0, 0, 0, 0)';

  const bag = () => document.querySelector('cart-items.cart-wrapper');
  const cartApi = () => {
    const element = bag();
    return element && typeof element.writeLines === 'function' ? element : null;
  };
  const motion = () => !REDUCED.matches;
  const runOn = () => {
    const element = bag();
    return motion() && !(element && element.dataset.run === 'false');
  };
  // Ids go out as numbers (Number(dataset…)), never as the attribute's string: one JSON type per field, so a
  // server-side mapper or a warehouse schema sees `variant` the same way on every bag event.
  const track = (name, props) => {
    try {
      if (typeof window.sofsyTrack === 'function') window.sofsyTrack(name, props || {});
    } catch (_) {
      /* tracking never breaks the bag */
    }
  };
  // bag.update.fail {action, reason} (taxonomy v2.0 r6.1): every bag write that failed, whatever the bag then shows.
  // action: quantity | remove | swap (+ what: colour | size) | undo | complete_set (+ set: cts | care); the extras
  // are fixed theme words. reason: bag.items.add_fail's list,
  // read the same way (assets/sofsy-track.js failReason): the answer's flags and status, and for a 422 Shopify's own
  // sentence (never anything she typed).
  const failReason = (result) => {
    const r = result || {};
    if (r.rateLimited || r.status === 429) return 'rate_limited';
    if (r.network) return 'network';
    if (r.soldOut) return 'sold_out';
    if (r.status === 422) {
      const words = String(r.description || '').toLowerCase();
      if (/sold out/.test(words)) return 'sold_out';
      if (/\bonly \d+|availability|can't add more|cannot add more|can only add|maximum|not enough/.test(words)) return 'stock_limit';
    }
    return 'other';
  };
  const failed = (action, result, extra) => track('bag.update.fail', Object.assign({ action, reason: failReason(result) }, extra || {}));
  const animate = (element, keyframes, options) => {
    if (!element || typeof element.animate !== 'function') return null;
    try {
      return element.animate(keyframes, options);
    } catch (_) {
      return null;
    }
  };
  const done = (animation) => (animation && animation.finished ? animation.finished.catch(() => {}) : Promise.resolve());

  let strings = null;
  const words = () => {
    if (strings) return strings;
    try {
      strings = JSON.parse((bag() && bag().querySelector('script[data-bag-strings]').textContent) || '{}');
    } catch (_) {
      strings = {};
    }
    return strings;
  };

  /* ---------- money: the distances '$15' and '$110' (Intl in the cart's currency; US only, #43) ---------- */
  const state = () => {
    const element = bag();
    try {
      return element && typeof element.readBagState === 'function' ? element.readBagState() : null;
    } catch (_) {
      return null;
    }
  };
  const money = (cents, currency) => {
    const value = Number(cents) / 100;
    if (!Number.isFinite(value)) return '';
    const whole = Math.round(value) === value;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2
      }).format(value);
    } catch (_) {
      return String(value);
    }
  };
  const paintMoney = (root) => {
    if (!root) return;
    const currency = (state() || {}).currency || 'USD';
    root.querySelectorAll('[data-bag-money]').forEach((element) => {
      element.textContent = money(element.dataset.bagMoney, currency);
    });
  };

  /* =================================================================================================
   * B02: the delivery run
   * ================================================================================================= */
  const topEl = () => bag() && bag().querySelector('[data-bag-top]');
  const topValues = (element) => {
    const total = Number(element.dataset.total) || 0;
    const threshold = Number(element.dataset.threshold) || 0;
    const share = threshold > 0 ? Math.min(1, Math.max(0, total / threshold)) : 0;
    return { total, threshold, share, reached: threshold > 0 && total >= threshold };
  };
  const vanBefore = () => {
    const raw = bag() ? parseFloat(getComputedStyle(bag()).getPropertyValue('--bag-van-before')) : NaN;
    return Number.isFinite(raw) ? Math.min(1, Math.max(0, raw)) : 0.3;
  };

  // '$110' sits under the dot, centred on it, but never past either end of the track (bt5_prog's clamp).
  const placeAmount = (top, share) => {
    const track = top.querySelector('[data-bag-track]');
    const amount = top.querySelector('[data-bag-amount]');
    if (!track || !amount) return;
    const width = track.clientWidth;
    const own = amount.offsetWidth;
    if (!width || !own) return;
    const left = Math.max(0, Math.min(width - own, share * width - own / 2));
    amount.style.left = `${left}px`;
    amount.classList.add('is-placed');
  };

  let openRunEndsAt = 0;
  const topParts = (top) => ({
    fill: top.querySelector('[data-bag-fill]'),
    runner: top.querySelector('[data-bag-runner]'),
    dot: top.querySelector('[data-bag-dot]'),
    amount: top.querySelector('[data-bag-amount]'),
    van: top.querySelector('[data-bag-van]'),
    sentence: top.querySelector('[data-bag-sentence]')
  });

  // from: {share, reached, amountText, amountLeft} or null (the start of the line); delay in ms; oldWords: the
  // sentence's previous HTML when the words changed.
  function runTop(from, delay, oldWords) {
    const top = topEl();
    if (!top) return;
    paintMoney(top);
    const now = topValues(top);
    placeAmount(top, now.share);
    const parts = topParts(top);
    Object.values(parts).forEach((element) => {
      if (element && typeof element.getAnimations === 'function') element.getAnimations().forEach((a) => a.cancel());
    });
    if (!runOn()) return;

    const start = from || { share: 0, reached: false };
    const moving = start.share !== now.share || start.reached !== now.reached;
    const up = !start.reached && now.reached;
    const down = start.reached && !now.reached;
    const D = T.run();
    const dl = Math.max(0, delay || 0);
    const run = { duration: D, delay: dl, easing: T.ease(), fill: 'backwards' };

    if (moving) {
      animate(parts.fill, [{ transform: `scaleX(${start.share})` }, { transform: `scaleX(${now.share})` }], run);
      animate(parts.runner, [{ transform: `translateX(${start.share * 100}%)` }, { transform: `translateX(${now.share * 100}%)` }], run);
      animate(parts.dot, [{ boxShadow: HALO }, { boxShadow: HALO, offset: 0.8 }, { boxShadow: NO_HALO }], {
        duration: D,
        delay: dl,
        easing: 'linear',
        fill: 'backwards'
      });
      if (up) {
        animate(parts.dot, [{ opacity: 1 }, { opacity: 1, offset: D / (D + T.dotOut) }, { opacity: 0 }], {
          duration: D + T.dotOut,
          delay: dl,
          easing: 'linear',
          fill: 'backwards'
        });
      } else if (down) {
        animate(parts.dot, [{ opacity: 0 }, { opacity: 1 }], { duration: T.dotOut, delay: dl, easing: 'linear', fill: 'backwards' });
      }
      if (!now.reached) {
        animate(parts.amount, [{ opacity: 0, transform: 'translateY(2px)' }, { opacity: 1, transform: 'none' }], {
          duration: T.label,
          delay: dl + D,
          easing: 'ease-out',
          fill: 'backwards'
        });
      } else if (up && from && from.share > 0 && parts.amount && from.amountText) {
        // the old figure leaves as the run starts (bt5_prog: 'bt-out .15s' at the delay)
        parts.amount.textContent = from.amountText;
        if (from.amountLeft) parts.amount.style.left = from.amountLeft;
        animate(parts.amount, [{ opacity: 1 }, { opacity: 0 }], { duration: T.dotOut, delay: dl, easing: 'linear', fill: 'backwards' });
      }
      const before = vanBefore();
      if (up) {
        animate(parts.van, [{ opacity: before }, { opacity: 1 }], { duration: T.van, delay: dl + D, easing: 'linear', fill: 'backwards' });
      } else if (down) {
        animate(parts.van, [{ opacity: 1 }, { opacity: before }], { duration: T.van, delay: dl, easing: 'linear', fill: 'backwards' });
      }
    }

    if (oldWords && parts.sentence && oldWords !== parts.sentence.innerHTML) {
      const at = up ? dl + D : dl;
      const ghost = document.createElement('span');
      ghost.className = 'bag-top__ghost';
      ghost.setAttribute('aria-hidden', 'true');
      ghost.innerHTML = oldWords;
      parts.sentence.appendChild(ghost);
      const out = animate(ghost, [{ opacity: 1 }, { opacity: 0 }], { duration: T.words(), delay: at, easing: 'linear', fill: 'both' });
      Array.from(parts.sentence.childNodes)
        .filter((node) => node !== ghost && node.nodeType === 1)
        .forEach((node) => {
          animate(node, [{ opacity: 0 }, { opacity: 1 }], { duration: T.words(), delay: at + T.words(), easing: 'linear', fill: 'backwards' });
        });
      parts.sentence.classList.add('is-crossfading');
      done(out).then(() => {
        ghost.remove();
        parts.sentence.classList.remove('is-crossfading');
      });
    }
  }

  // The end state with nothing moving (a render while the bag is closed; the next open runs from the start).
  function settleTop() {
    const top = topEl();
    if (!top) return;
    paintMoney(top);
    placeAmount(top, topValues(top).share);
    Object.values(topParts(top)).forEach((element) => {
      if (element && typeof element.getAnimations === 'function') element.getAnimations().forEach((a) => a.cancel());
    });
  }

  // B5 (zao#617): the delivery row and bar now live in the pinned bottom ([data-bag-figures]), so what a render
  // replaced is looked for in the old body first (today's top) and then in the old figures (cart.js prevFigures).
  const previousPart = (selector, prevBody, prevFigures) =>
    (prevBody && prevBody.querySelector(selector)) || (prevFigures && prevFigures.querySelector(selector)) || null;

  // What the top showed before a render: from the bag state the render replaced, and the amount's place.
  function previousTop(prev, prevBody, prevFigures) {
    if (!prev || !prev.is_us || !prev.threshold || !prev.item_count) return null;
    const share = Math.min(1, Math.max(0, prev.total_price / prev.threshold));
    const oldTop = previousPart('[data-bag-top]', prevBody, prevFigures);
    const oldAmount = oldTop && oldTop.querySelector('[data-bag-amount]');
    return {
      share,
      reached: prev.total_price >= prev.threshold,
      amountText: oldAmount ? oldAmount.textContent : '',
      amountLeft: oldAmount ? oldAmount.style.left : ''
    };
  }

  /* =================================================================================================
   * Busy, status, small helpers
   * ================================================================================================= */
  let busy = false;
  function setBusy(on) {
    busy = on;
    const element = bag();
    if (!element) return;
    // An add drawn into the bag before its answer (cart.js pendingAdd, zao#617) keeps the lines still too.
    element.classList.toggle('is-busy', on || !!element.pendingAdd);
    const lines = element.querySelector('[data-bag-body]');
    if (lines) lines.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  let statusTimer = 0;
  let statusStays = false; // zao#617: a refused add's sentence stays until her next tap in the bag, or the close
  function say(text, options) {
    const element = bag() && bag().querySelector('[data-bag-status]');
    if (!element) return;
    clearTimeout(statusTimer);
    element.textContent = text || '';
    element.hidden = !text;
    statusStays = !!(text && options && options.stay);
    if (text && !statusStays) {
      statusTimer = setTimeout(() => {
        element.textContent = '';
        element.hidden = true;
      }, 6000);
    }
  }

  const lineOf = (element) => element && element.closest('[data-bag-line]');
  // How many of a variant a bag state holds (a render after a FAILED write must not play the success motion).
  const held = (bagState, variantId) =>
    (bagState && Array.isArray(bagState.lines) ? bagState.lines : [])
      .filter((line) => Number(line.variant_id) === Number(variantId))
      .reduce((sum, line) => sum + Number(line.quantity || 0), 0);
  const lineByVariant = (variantId) =>
    bag() && bag().querySelector(`[data-bag-line][data-variant-id="${String(variantId).replace(/[^0-9]/g, '')}"]`);
  const fill = (template, values) =>
    String(template || '').replace(/%(\w+)%/g, (_, name) => (values[name] !== undefined ? values[name] : ''));

  /* =================================================================================================
   * B04: a line stays where she left it (bag_lines.json: "the line should stay where she left it"; "Undo re-adds
   * the same variant and quantity in the same place")
   *
   * Shopify lists the newest line first, so a colour or size change (a new variant added, the old line taken away)
   * and an Undo (the line added back) both come back as the NEWEST line, and the Liquid order (styles newest first,
   * oldest line first inside a style) would draw that line at the bottom of its style, or move its whole style to
   * the top. The bag therefore remembers the order it last showed (variant ids, in this tab's sessionStorage, so a
   * new page and every later re-render keep it) and puts the lines back in that order after each render:
   *   - a change: the new variant takes the old line's place (onto a shade or size she already has, the one merged
   *     line takes the place of the line she changed);
   *   - Undo: the line goes back after the line that was above it (or first, if it was first);
   *   - a NEW line keeps today's rule: under the last line of its style (B10 N1: a Complete the Set piece lands
   *     under the line it completes); a style new to the bag starts at the top, as Shopify lists it;
   *   - lines that left the bag are forgotten at once.
   * Only the order on screen changes: the bag state JSON, the line keys and every cart write are untouched.
   * ================================================================================================= */
  const ORDER_KEY = 'sofsy:bag-order';
  let orderFallback = []; // when sessionStorage cannot be used (private mode, blocked site data)
  let pendingOrder = null; // {type:'swap', from, to} | {type:'undo', variant, after, group, index}

  function loadOrder() {
    try {
      const parsed = JSON.parse(window.sessionStorage.getItem(ORDER_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.map(String).filter((id) => /^\d+$/.test(id)) : [];
    } catch (_) {
      return orderFallback.slice();
    }
  }

  function saveOrder(ids) {
    orderFallback = ids.slice();
    try {
      window.sessionStorage.setItem(ORDER_KEY, JSON.stringify(ids));
    } catch (_) {
      /* the in-page copy is enough for this page */
    }
  }

  function orderLines(intent) {
    const root = bag();
    if (!root) return;
    const list = root.querySelector('[data-bag-lines]');
    if (!list) {
      if (root.querySelector('[data-bag-body]')) saveOrder([]); // an empty bag remembers nothing
      return;
    }
    const lines = Array.from(list.children).filter((child) => child.matches('[data-bag-line]'));
    const elementsOf = new Map(); // variant id -> its line(s); two lines of one variant (a legacy gift) stay together
    const groupOf = new Map();
    const shown = [];
    lines.forEach((line) => {
      const id = String(line.dataset.variantId || '');
      if (!elementsOf.has(id)) {
        elementsOf.set(id, []);
        groupOf.set(id, line.dataset.bagGroup || '');
        shown.push(id);
      }
      elementsOf.get(id).push(line);
    });

    let order = loadOrder();
    if (intent && intent.type === 'swap') {
      const from = String(intent.from);
      const to = String(intent.to);
      if (order.includes(from)) {
        order = order.filter((id) => id !== to);
        const at = order.indexOf(from);
        // The old line still here means its removal failed (both lines are in the bag): the new one goes under it.
        if (elementsOf.has(from)) order.splice(at + 1, 0, to);
        else order.splice(at, 1, to);
      }
    } else if (intent && intent.type === 'undo') {
      const id = String(intent.variant);
      if (!order.includes(id)) {
        let at = -1;
        if (intent.after === null) at = 0;
        else {
          const above = order.indexOf(String(intent.after));
          if (above >= 0) at = above + 1;
        }
        if (at < 0) {
          const start = order.findIndex((other) => groupOf.get(other) === intent.group);
          at = start >= 0 ? start : Math.min(Number(intent.index) || 0, order.length);
        }
        order.splice(at, 0, id);
      }
    }

    const seen = new Set();
    order = order.filter((id) => elementsOf.has(id) && !seen.has(id) && seen.add(id));
    const fresh = shown.filter((id) => !seen.has(id));
    if (!order.length) {
      order = shown.slice(); // nothing remembered yet: Shopify's order, as Liquid drew it
    } else if (fresh.length) {
      const newStyles = [];
      fresh.forEach((id) => {
        let last = -1;
        order.forEach((other, index) => {
          if (groupOf.get(other) === groupOf.get(id)) last = index;
        });
        if (last >= 0) order.splice(last + 1, 0, id);
        else newStyles.push(id);
      });
      order = newStyles.concat(order);
    }
    // A style's lines always sit together (Complete the Set and the hairlines rely on it).
    const styles = [];
    const byStyle = new Map();
    order.forEach((id) => {
      const group = groupOf.get(id);
      if (!byStyle.has(group)) {
        byStyle.set(group, []);
        styles.push(group);
      }
      byStyle.get(group).push(id);
    });
    order = [].concat(...styles.map((group) => byStyle.get(group)));

    if (order.join('|') !== shown.join('|')) {
      const others = Array.from(list.children).filter((child) => !child.matches('[data-bag-line]'));
      order.forEach((id) => elementsOf.get(id).forEach((line) => list.appendChild(line)));
      others.forEach((other) => {
        // Complete the Set stays under the last line of the style it completes; an Undo row goes back in place.
        if (other.matches('[data-bag-cts]')) {
          const group = other.dataset.bagGroup || '';
          const own = Array.from(list.children).filter((child) => child.matches('[data-bag-line]') && (child.dataset.bagGroup || '') === group);
          const last = own[own.length - 1];
          if (last) last.after(other);
        } else if (other.matches('[data-bag-undo-row]')) {
          const entry = undoRows.find((row) => row.element === other);
          if (entry) placeUndoRow(other, entry);
        }
      });
    }
    saveOrder(order);
  }

  // Keep a control she is about to use inside the bag's visible scroll area (above the pinned bottom; under SC1's
  // returning header when it is showing).
  function revealInBag(element) {
    const root = bag();
    const scroller = root && root.querySelector('[data-bag-scroll]');
    if (!scroller || !element || !scroller.contains(element)) return;
    const box = element.getBoundingClientRect();
    const view = scroller.getBoundingClientRect();
    if (!box.height && !box.width) return;
    const head = root.querySelector('[data-bag-head].is-returning');
    const topEdge = view.top + (head ? head.offsetHeight : 0) + 8;
    const bottomEdge = view.bottom - 8;
    let delta = 0;
    if (box.top < topEdge) delta = box.top - topEdge;
    else if (box.bottom > bottomEdge) delta = Math.min(box.bottom - bottomEdge, box.top - topEdge);
    if (delta) scroller.scrollTo({ top: scroller.scrollTop + delta, behavior: motion() ? 'smooth' : 'auto' });
  }

  // After a render: put focus back on the same control of the same line (or another sensible place), and keep it
  // in sight.
  let focusAfter = null;
  function restoreFocus() {
    const want = focusAfter;
    focusAfter = null;
    if (!want || !bag() || !bag().isDrawerOpen()) return;
    let target = null;
    if (want.variant) {
      const line = lineByVariant(want.variant);
      if (line && want.selector) target = line.querySelector(want.selector);
      if (!target && line) target = line.querySelector('[data-bag-remove]');
    }
    if (!target && want.selector && !want.variant) target = bag().querySelector(want.selector);
    const fallback = !target;
    if (!target) target = bag().querySelector('#BagTitle');
    try {
      target && target.focus({ preventScroll: true });
    } catch (_) {
      /* not focusable */
    }
    if (target && !fallback) revealInBag(target);
  }

  /* =================================================================================================
   * B04: rows that open inside the line (O1, C24, P30, MW)
   * ================================================================================================= */
  let openRow = null; // {line, kind}

  function scrollIntoBag(element) {
    const scroller = bag() && bag().querySelector('[data-bag-scroll]');
    if (!scroller || !element) return;
    const box = element.getBoundingClientRect();
    const view = scroller.getBoundingClientRect();
    const below = box.bottom - view.bottom;
    if (below > 0) scroller.scrollTo({ top: scroller.scrollTop + below, behavior: motion() ? 'smooth' : 'auto' });
  }

  function openPanel(line, kind, byKeyboard) {
    closeAsk();
    if (openRow && (openRow.line !== line || openRow.kind !== kind)) closePanel(true);
    const panel = line.querySelector(`[data-bag-panel="${kind}"]`);
    const button = line.querySelector(`[data-bag-open="${kind}"]`);
    if (!panel || !button) return;
    panel.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    line.classList.add(`is-open-${kind}`);
    openRow = { line, kind };
    if (motion()) {
      const height = panel.scrollHeight;
      panel.classList.add('is-moving');
      const grow = animate(panel, [{ height: '0px' }, { height: `${height}px` }], { duration: T.panel, easing: 'ease-out' });
      animate(panel.firstElementChild, [{ opacity: 0 }, { opacity: 1 }], { duration: T.words(), easing: 'linear' });
      done(grow).then(() => {
        panel.classList.remove('is-moving');
        scrollIntoBag(panel);
      });
    } else {
      scrollIntoBag(panel);
    }
    if (byKeyboard) {
      const chosen = panel.querySelector('[aria-checked="true"]') || panel.querySelector('[role="radio"]');
      if (chosen) chosen.focus({ preventScroll: true });
    }
    track('bag_line_change_opened', { what: kind, variant: Number(line.dataset.variantId) || null });
  }

  function closePanel(animated) {
    if (!openRow) return;
    const { line, kind } = openRow;
    openRow = null;
    const panel = line.querySelector(`[data-bag-panel="${kind}"]`);
    const button = line.querySelector(`[data-bag-open="${kind}"]`);
    line.classList.remove(`is-open-${kind}`);
    if (button) button.setAttribute('aria-expanded', 'false');
    if (!panel || panel.hidden) return;
    if (animated && motion() && line.isConnected) {
      const height = panel.offsetHeight;
      panel.classList.add('is-moving');
      const shrink = animate(panel, [{ height: `${height}px` }, { height: '0px' }], { duration: T.panel, easing: 'ease-out' });
      animate(panel.firstElementChild, [{ opacity: 1 }, { opacity: 0 }], { duration: T.words(), easing: 'linear', fill: 'forwards' });
      done(shrink).then(() => {
        panel.hidden = true;
        panel.classList.remove('is-moving');
        if (panel.firstElementChild) panel.firstElementChild.getAnimations().forEach((a) => a.cancel());
      });
    } else {
      panel.hidden = true;
    }
  }

  /* ---------- the slow sold-out message in a row (Ben: "fade in and out slowly enough to read") ---------- */
  const notes = new WeakMap();
  function showNote(line, kind, text) {
    const row = line && line.querySelector(`[data-bag-row="${kind}"]`);
    if (!row) return;
    const note = row.querySelector('[data-bag-note]');
    const face = row.querySelector('.bag-line__change, .bag-line__words');
    if (!note || !face) return;
    const running = notes.get(row);
    if (running) {
      clearTimeout(running.timer);
      running.animations.forEach((a) => a && a.cancel());
    }
    note.textContent = text;
    row.classList.add('has-note');
    const fadeIn = motion() ? T.noteIn() : 0;
    const fadeOut = motion() ? T.noteOut() : 0;
    const animations = [
      animate(face, [{ opacity: 1 }, { opacity: 0 }], { duration: fadeIn, easing: 'linear', fill: 'forwards' }),
      animate(note, [{ opacity: 0 }, { opacity: 1 }], { duration: fadeIn, easing: 'linear', fill: 'forwards' })
    ];
    const entry = { animations, timer: 0 };
    notes.set(row, entry);
    entry.timer = setTimeout(() => {
      entry.animations.forEach((a) => a && a.cancel());
      entry.animations = [
        animate(face, [{ opacity: 0 }, { opacity: 1 }], { duration: fadeOut, easing: 'linear' }),
        animate(note, [{ opacity: 1 }, { opacity: 0 }], { duration: fadeOut, easing: 'linear', fill: 'forwards' })
      ];
      Promise.all(entry.animations.map(done)).then(() => {
        if (notes.get(row) !== entry) return;
        row.classList.remove('has-note');
        note.textContent = '';
        entry.animations.forEach((a) => a && a.cancel());
        notes.delete(row);
      });
    }, fadeIn + T.noteHold());
  }

  /* ---------- a colour or size change (one tap applies: O1) ---------- */
  let pendingSwap = null;
  function swapTo(line, kind, choice) {
    const api = cartApi();
    if (!api || busy) return;
    const from = Number(line.dataset.variantId);
    const to = Number(kind === 'colour' ? choice.dataset.bagShade : choice.dataset.bagSizeVariant);
    // #63 A1: a drawn line that stands for split lines changes only its own Shopify line's quantity (read from the
    // bag state by key); the twin stays where it is and is drawn on its own afterwards. Nothing added, nothing lost.
    let quantity = Number(line.dataset.quantity);
    if (String(line.dataset.lineKeys || '').indexOf(',') !== -1) {
      const s = state();
      const own = s && Array.isArray(s.lines) ? s.lines.find((l) => l.key === line.dataset.lineKey) : null;
      if (!own) return;
      quantity = Number(own.quantity);
    }
    const shade = kind === 'colour' ? choice.dataset.shadeName : line.dataset.shadeName;
    const size = kind === 'size' ? choice.dataset.sizeLabel : line.dataset.sizeLabel;
    if (!to || to === from) {
      closePanel(true);
      return;
    }
    if (choice.dataset.max === '0') {
      showNote(line, kind, fill(words().sold_out_note, { shade, size }));
      return;
    }

    // Applied at once (O1): the row closes and says the new choice while Shopify confirms.
    const row = line.querySelector(`[data-bag-row="${kind}"] [data-bag-words]`);
    const was = row ? row.textContent : '';
    if (row) row.textContent = kind === 'colour' ? shade : size;
    closePanel(true);
    const photo = line.querySelector('[data-bag-photo]');
    pendingSwap = { to, kind, photo: kind === 'colour' && photo ? photo.cloneNode(true) : null };
    pendingOrder = { type: 'swap', from, to }; // the new line takes this line's place
    focusAfter = { variant: to, selector: `[data-bag-open="${kind}"]` };
    setBusy(true);
    api
      .swapLine(
        { key: line.dataset.lineKey, variantId: to, quantity, fromVariantId: from },
        { source: kind === 'colour' ? 'bag_colour' : 'bag_size' }
      )
      .then((result) => {
        setBusy(false);
        pendingOrder = null;
        if (result && result.ok) {
          track(kind === 'colour' ? 'bag_colour_changed' : 'bag_size_changed', { from, to, quantity });
          return;
        }
        pendingSwap = null;
        focusAfter = null;
        failed('swap', result, { what: kind });
        const current = lineByVariant(from) || line;
        const currentRow = current.querySelector(`[data-bag-row="${kind}"] [data-bag-words]`);
        if (currentRow && currentRow.textContent !== was) currentRow.textContent = was;
        if (result && result.soldOut) {
          showNote(current, kind, fill(words().sold_out_note, { shade, size }));
        } else {
          say((result && result.description) || '');
        }
        const back = current.querySelector(`[data-bag-open="${kind}"]`);
        if (back) back.focus({ preventScroll: true });
      });
  }

  // #63 A1: one drawn line can stand for several Shopify lines of the same variant (Shopify splits a line when a
  // discount covers only part of its quantity; sections/cart-drawer.liquid draws them as one, with every key in
  // data-line-keys). A write sets the whole quantity on the drawn line's key and every other key to 0, in ONE
  // request, so + never leaves a second line behind and remove takes them all; Shopify re-splits as its discount says.
  function lineUpdates(line, quantity) {
    const keys = String(line.dataset.lineKeys || line.dataset.lineKey || '').split(',').filter(Boolean);
    if (!keys.length) keys.push(line.dataset.lineKey);
    const updates = {};
    keys.forEach((key, index) => {
      updates[key] = index === 0 ? quantity : 0;
    });
    return updates;
  }

  /* ---------- the stepper (Q-12: min(15, stock); at one pair − does nothing) ---------- */
  function step(line, delta, button) {
    const api = cartApi();
    if (!api || busy) return;
    const quantity = Number(line.dataset.quantity);
    const cap = Number(line.dataset.cap) || 15;
    if (delta < 0 && quantity <= 1) return;
    if (delta > 0 && quantity >= cap) return;
    const next = quantity + delta;
    const value = line.querySelector('[data-bag-qty]');
    if (value) value.textContent = String(next);
    atFloor(line, next);
    focusAfter = { variant: Number(line.dataset.variantId), selector: `[data-bag-step="${delta > 0 ? 1 : -1}"]` };
    setBusy(true);
    api.writeLines(lineUpdates(line, next), { source: 'bag_stepper' }).then((result) => {
      setBusy(false);
      // Every stepper write, kept or refused (taxonomy v2.0, zao#455): Shopify sends nothing for the bag's "-".
      track('bag.line.qty_change', {
        variant: Number(line.dataset.variantId) || null,
        from: quantity,
        to: next,
        direction: delta > 0 ? 'up' : 'down',
        ok: !!(result && result.ok)
      });
      if (result && result.ok) return;
      focusAfter = null;
      failed('quantity', result);
      const current = lineByVariant(line.dataset.variantId) || line;
      const shown = current.querySelector('[data-bag-qty]');
      if (shown) shown.textContent = current.dataset.quantity;
      atFloor(current, Number(current.dataset.quantity));
      say((result && result.description) || '');
      if (button && button.isConnected) button.focus({ preventScroll: true });
    });
  }

  // A11: the − is grey and inert at one pair. cart-item.liquid renders it so; this keeps it in step with the number
  // shown while a stepper write is in flight (and puts it back if the write fails), until the bag re-renders.
  function atFloor(line, quantity) {
    const minus = line.querySelector('[data-bag-step="-1"]');
    if (!minus) return;
    const floor = quantity <= 1;
    minus.toggleAttribute('data-at-floor', floor);
    if (floor) minus.setAttribute('aria-disabled', 'true');
    else minus.removeAttribute('aria-disabled');
  }

  /* ---------- remove: ask, then Undo for 5 s (RM3 + RM2) ---------- */
  let askLine = null;
  function ask(line) {
    if (askLine === line) {
      closeAsk(true);
      return;
    }
    closeAsk();
    closePanel(true);
    const question = line.querySelector('[data-bag-ask]');
    const foot = line.querySelector('[data-bag-foot]');
    if (!question || !foot) return;
    foot.hidden = true;
    question.hidden = false;
    askLine = line;
    animate(question, [{ opacity: 0 }, { opacity: 1 }], { duration: motion() ? T.words() : 0, easing: 'linear' });
    const keep = question.querySelector('[data-bag-remove-keep]');
    if (keep) keep.focus({ preventScroll: true });
    track('bag_line_remove_asked', { variant: Number(line.dataset.variantId) || null });
  }

  function closeAsk(focusX) {
    if (!askLine) return;
    const line = askLine;
    askLine = null;
    const question = line.querySelector('[data-bag-ask]');
    const foot = line.querySelector('[data-bag-foot]');
    if (question) question.hidden = true;
    if (foot) foot.hidden = false;
    if (focusX) {
      const x = line.querySelector('[data-bag-remove]');
      if (x) x.focus({ preventScroll: true });
    }
  }

  let undoRows = []; // {variant, quantity, text, after (variant id of the line before it) or null, group, height, expires, element}
  let pendingRemove = null;
  function removeLine(line) {
    const api = cartApi();
    if (!api || busy) return;
    const lines = Array.from(bag().querySelectorAll('[data-bag-line]'));
    const index = lines.indexOf(line);
    const before = index > 0 ? lines[index - 1] : null;
    // What she removed, read now, at the tap. The write's own re-render (renderContents -> sofsy:bag-rendered, below)
    // takes and clears pendingRemove before the write's promise resolves, and the line has left the bag by then, so
    // neither can tell the tracker afterwards which variant went (s08 review M1: it sent variant null).
    const removedVariant = Number(line.dataset.variantId) || null;
    const removedProduct = Number(line.dataset.productId) || null;
    pendingRemove = {
      variant: removedVariant,
      quantity: Number(line.dataset.quantity),
      text: line.dataset.removedText,
      after: before ? Number(before.dataset.variantId) : null,
      group: line.dataset.bagGroup || '',
      index,
      height: line.offsetHeight
    };
    askLine = null;
    setBusy(true);
    api.writeLines(lineUpdates(line, 0), { source: 'bag_remove' }).then((result) => {
      setBusy(false);
      if (result && result.ok) {
        // Dictionary 5.4: `variant`; product_id / variant_id are the common fields (the stand-in for Shopify's
        // missing product_removed_from_cart, so it must say what went).
        track('bag_line_removed', { variant: removedVariant, product_id: removedProduct });
        return;
      }
      pendingRemove = null;
      failed('remove', result);
      say((result && result.description) || '');
    });
  }

  function placeUndoRow(row, entry) {
    const body = bag() && bag().querySelector('[data-bag-body]');
    if (!body) return false;
    const lines = body.querySelector('[data-bag-lines]');
    if (lines) {
      const after = entry.after ? lines.querySelector(`[data-bag-line][data-variant-id="${entry.after}"]`) : null;
      if (after) {
        after.after(row);
      } else {
        const sameGroup = entry.group ? lines.querySelector(`[data-bag-line][data-bag-group="${entry.group}"]`) : null;
        lines.insertBefore(row, sameGroup || lines.firstChild);
      }
      return true;
    }
    // The last line went: "BAG (0)" … "Your bag is empty." under the Undo row.
    const empty = body.querySelector('.bag__empty');
    body.insertBefore(row, empty || null);
    return true;
  }

  function buildUndoRow(entry) {
    const row = document.createElement('div');
    row.className = 'bag-undo';
    row.setAttribute('data-bag-undo-row', '');
    row.setAttribute('role', 'status');
    row.setAttribute('aria-live', 'polite');
    const text = document.createElement('span');
    text.className = 'bag-undo__words';
    text.textContent = entry.text || '';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bag-undo__button';
    button.setAttribute('data-bag-undo', String(entry.variant));
    button.setAttribute('data-sofsy-track', 'bag.line.undo.tap');
    button.textContent = words().undo || 'Undo';
    const tap = document.createElement('i');
    tap.className = 'ds-tap';
    tap.setAttribute('aria-hidden', 'true');
    button.appendChild(tap);
    row.append(text, button);
    entry.element = row;
    return row;
  }

  function endUndo(entry, animated) {
    clearTimeout(entry.timer);
    undoRows = undoRows.filter((other) => other !== entry);
    const row = entry.element;
    if (!row || !row.isConnected) return;
    if (animated && motion()) {
      const shrink = animate(row, [{ height: `${row.offsetHeight}px`, opacity: 1 }, { height: '0px', opacity: 0 }], {
        duration: T.collapse,
        easing: 'ease-out',
        fill: 'forwards'
      });
      done(shrink).then(() => row.remove());
    } else {
      row.remove();
    }
  }

  function startUndo(entry) {
    const row = buildUndoRow(entry);
    if (!placeUndoRow(row, entry)) return;
    entry.expires = Date.now() + T.undo;
    entry.timer = setTimeout(() => endUndo(entry, true), T.undo);
    undoRows.push(entry);
    if (motion() && entry.height) {
      row.classList.add('is-arriving');
      const collapse = animate(row, [{ height: `${entry.height}px` }, { height: `${row.scrollHeight}px` }], {
        duration: T.collapse,
        easing: 'ease-out'
      });
      animate(row.firstElementChild, [{ opacity: 0 }, { opacity: 1 }], { duration: T.collapse, easing: 'linear' });
      done(collapse).then(() => row.classList.remove('is-arriving'));
    }
    const button = row.querySelector('[data-bag-undo]');
    if (button) button.focus({ preventScroll: true });
  }

  function undo(entry) {
    const api = cartApi();
    if (!api || busy || !entry) return;
    setBusy(true);
    focusAfter = { variant: entry.variant, selector: '[data-bag-remove]' };
    // Back in the same place: after the line that was above it (or first in the bag if it was first).
    pendingOrder = { type: 'undo', variant: entry.variant, after: entry.after, group: entry.group, index: entry.index };
    api.addItems([{ id: entry.variant, quantity: entry.quantity }], { source: 'undo' }).then((result) => {
      setBusy(false);
      pendingOrder = null;
      if (result && result.ok) {
        track('bag_line_undone', { variant: entry.variant, quantity: entry.quantity });
        endUndo(entry, false);
        restoreFocus();
        return;
      }
      focusAfter = null;
      failed('undo', result);
      say((result && result.description) || '');
    });
  }

  /* =================================================================================================
   * B10: Complete the Set in the bag, and #63 A16: The Care Set (the same behaviour, its own hooks and events)
   * ================================================================================================= */
  const SET_HOOKS = {
    cts: { block: '[data-bag-cts]', card: '[data-bag-cts-card]', source: 'bag_cts', linkSource: 'bag_cts_link' },
    care: { block: '[data-bag-care]', card: '[data-bag-care-card]', source: 'bag_care', linkSource: 'bag_care_link' }
  };
  let pendingCts = null; // {kind: 'cts' | 'care', variants: [ids], link: bool, group}
  function ctsAdd(button, viaLink, kind) {
    const api = cartApi();
    if (!api || busy) return;
    const setKind = kind === 'care' ? 'care' : 'cts';
    const hooks = SET_HOOKS[setKind];
    const block = button.closest(hooks.block);
    if (!block) return;
    let ids;
    let names;
    if (viaLink) {
      ids = String(button.dataset.variantIds || '')
        .split(',')
        .map((id) => Number(id))
        .filter(Boolean);
      names = button.dataset.shadeNames || '';
    } else {
      const card = button.closest(hooks.card);
      ids = card ? [Number(card.dataset.variantId)] : [];
      names = card ? card.dataset.shadeName : '';
    }
    if (!ids.length) return;
    pendingCts = { kind: setKind, variants: ids, link: viaLink, group: block.dataset.bagGroup || '' };
    (viaLink ? block : button).classList.add('is-adding');
    if (viaLink) button.setAttribute('aria-busy', 'true');
    setBusy(true);
    if (setKind === 'cts') track(viaLink ? 'complete_set_link_tapped' : 'complete_set_plus_tapped', { variants: ids, place: 'bag' });
    // ONE /cart/add.js for every card (cart.js addItems: one request, the _sofsy_storefront_cart attribute with it).
    api
      .addItems(
        ids.map((id) => ({ id, quantity: 1 })),
        { source: viaLink ? hooks.linkSource : hooks.source }
      )
      .then((result) => {
        setBusy(false);
        if (result && result.ok) {
          const props = { variants: ids, place: 'bag', via: viaLink ? 'link' : 'plus' };
          if (setKind === 'care') track('bag_care_set_add', props);
          else track('complete_set_added', props);
          return;
        }
        pendingCts = null;
        failed('complete_set', result, { set: setKind });
        const live = bag().querySelector(hooks.block);
        if (live) {
          live.classList.remove('is-adding');
          live.querySelectorAll('.is-adding').forEach((element) => element.classList.remove('is-adding'));
          live.querySelectorAll('[aria-busy]').forEach((element) => element.removeAttribute('aria-busy'));
        }
        say(fill(words().cts_failed, { shades: names }));
      });
  }

  // After the add's render: the card fades and the rest slide left; or the whole block folds.
  function ctsAfterRender(prevBody) {
    const job = pendingCts;
    pendingCts = null;
    if (!job || !motion()) return;
    const hooks = SET_HOOKS[job.kind] || SET_HOOKS.cts;
    const oldBlock = prevBody && prevBody.querySelector(hooks.block);
    if (!oldBlock) return;
    const body = bag().querySelector('[data-bag-body]');
    const fresh = body.querySelector(hooks.block);
    const ghost = oldBlock.cloneNode(true);
    ghost.classList.remove('is-adding');
    ghost.querySelectorAll('.is-adding').forEach((element) => element.classList.remove('is-adding'));
    ghost.setAttribute('aria-hidden', 'true');
    ghost.setAttribute('inert', '');

    if (fresh && !job.link) {
      // H1 + N1: the added card fades (200 ms), the cards after it slide one step left (240 ms).
      fresh.replaceWith(ghost);
      const cards = Array.from(ghost.querySelectorAll(hooks.card));
      const gone = cards.findIndex((card) => job.variants.includes(Number(card.dataset.variantId)));
      if (gone < 0) {
        ghost.replaceWith(fresh);
        return;
      }
      const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : 0;
      const motions = [animate(cards[gone], [{ opacity: 1 }, { opacity: 0 }], { duration: T.ctsFade, easing: 'linear', fill: 'forwards' })];
      cards.slice(gone + 1).forEach((card) => {
        motions.push(
          animate(card, [{ transform: 'none' }, { transform: `translateX(${-step}px)` }], {
            duration: T.ctsSlide,
            easing: 'ease-out',
            fill: 'forwards'
          })
        );
      });
      Promise.all(motions.map(done)).then(() => {
        if (ghost.isConnected) ghost.replaceWith(fresh);
      });
      return;
    }

    if (!fresh) {
      // J1: the set is complete: the block's height goes to 0 with its hairline (240 ms).
      if (job.kind === 'care') {
        // The Care Set folds where it was: under all the lines (and Complete the Set).
        const lineList = body.querySelector('[data-bag-lines]');
        if (!lineList) return;
        lineList.after(ghost);
      } else {
        const group = job.group;
        const lines = body.querySelectorAll(group ? `[data-bag-line][data-bag-group="${group}"]` : '[data-bag-line]');
        const last = lines[lines.length - 1];
        if (!last) return;
        last.after(ghost);
        track('complete_set_completed', { place: 'bag' });
      }
      const height = ghost.offsetHeight;
      const fold = animate(ghost, [{ height: `${height}px`, opacity: 1 }, { height: '0px', opacity: 0 }], {
        duration: T.ctsFold,
        easing: 'ease-out',
        fill: 'forwards'
      });
      done(fold).then(() => ghost.remove());
    }
  }

  /* =================================================================================================
   * Events from cart.js
   * ================================================================================================= */
  // Complete the Set and The Care Set, counted once per open as the bag shows them.
  const PENDING_HOLD_MS = 60000; // a pending open's run waits at its start for the answer (released long before)
  let pendingRun = false;
  let openAddFailed = false; // zao#617: this open came from an add Shopify refused outright
  let setViewsWaiting = false;
  const isOpen = () => {
    const root = bag();
    if (!root) return false;
    return typeof root.isDrawerOpen === 'function' ? root.isDrawerOpen() : !root.classList.contains('close-drawer');
  };
  const failedTag = () => (openAddFailed ? { add_failed: true } : {});
  function trackSetViews() {
    setViewsWaiting = false;
    const block = bag() && bag().querySelector('[data-bag-cts]');
    if (block) track('complete_set_viewed', Object.assign({ place: 'bag', cards: Number(block.dataset.cards) || 0 }, failedTag()));
    const care = bag() && bag().querySelector('[data-bag-care]');
    if (care) track('bag_care_set_view', Object.assign({ place: 'bag', cards: Number(care.dataset.cards) || 0 }, failedTag()));
  }

  // zao#617: the open bag.js has handled (the event's detail; cart.js keeps the same object as lastOpen), so an open
  // is never handled twice: once from the event, once from catchUpOpen.
  let heardOpen = null;
  document.addEventListener('sofsy:bag-opened', (event) => onBagOpened(event.detail, null));

  // openedAtClock: when cart.js opened it (performance.now()), for an open that happened before this file loaded;
  // null for an open heard now.
  function onBagOpened(detail, openedAtClock) {
    if (detail && detail === heardOpen) return;
    heardOpen = detail || null;
    const slide = (detail && detail.slideMs) || 0;
    const delay = motion() ? slide : 0;
    openRunEndsAt = Date.now() + delay;
    scrollMax = 0;
    orderLines(null); // in case anything re-drew the bag without telling us
    openAddFailed = false;
    // zao#617: opened before the add's answer. The run holds at its start (the line empty) until the answer, then
    // runs ONCE from the start to the new total (bag-rendered, or bag-add-settled when nothing was re-rendered),
    // exactly as an open after the answer did: never to the old total first.
    pendingRun = !!(detail && detail.pending);
    if (pendingRun) runTop(null, PENDING_HOLD_MS, null);
    // #63 A4: the bag shown again as she left it (back from checkout) does not replay the delivery run.
    else if (!(detail && detail.instant)) runTop(null, delay, null);
    watchCheckout(openedAtClock);
    // zao#617: opened before the add's answer: count the sets as the answer leaves them (sofsy:bag-add-settled).
    if (detail && detail.pending) setViewsWaiting = true;
    else trackSetViews();
  }

  // zao#617: an open that happened before this file loaded. Back from checkout's "Return to cart" (and a reload with
  // the bag open), cart.js opens the bag at once in connectedCallback and sends 'sofsy:bag-opened' before this file
  // has run (both are deferred, cart.js first), so nothing heard it: no Secure checkout watch, and bag_closed without
  // ms_open / checkout_seen. If the bag is open now and its open was not heard, it is handled as if the event had just
  // come, timed from cart.js's real open time. Run at the start, DOMContentLoaded and load (only ever once per open).
  function catchUpOpen() {
    const root = bag();
    const missed = root && root.lastOpen;
    if (!missed || !missed.detail || missed.detail === heardOpen || !isOpen()) return;
    onBagOpened(missed.detail, Number.isFinite(missed.at) ? missed.at : null);
  }

  // The run held by a pending open starts now: from the start of the line, after whatever is left of the slide.
  function releasePendingRun() {
    if (!pendingRun) return;
    pendingRun = false;
    runTop(null, Math.max(0, openRunEndsAt - Date.now()), null);
  }

  document.addEventListener('sofsy:bag-add-settled', (event) => {
    const detail = (event && event.detail) || {};
    // A full refusal: this open shows no added line. Its bag views are tagged add_failed so they never count as
    // real bag views (a partly kept add did change the bag: not tagged).
    openAddFailed = detail.ok === false && detail.cartChanged !== true;
    if (isOpen()) releasePendingRun();
    if (setViewsWaiting) trackSetViews();
  });

  document.addEventListener('sofsy:bag-rendered', (event) => {
    const detail = event.detail || {};
    const root = bag();
    if (!root) return;
    openRow = null;
    askLine = null;
    paintMoney(root.querySelector('[data-bag-body]'));
    paintMoney(root.querySelector('[data-bag-figures]')); // B5: the delivery row's '$5' and the '$120' under the line

    // The lines go back in the order she last saw (before the Undo row, Complete the Set and focus use it). A change
    // or an Undo moves its line only when this render shows it worked (more of that variant than before).
    let intent = null;
    if (pendingOrder) {
      const variant = pendingOrder.type === 'swap' ? pendingOrder.to : pendingOrder.variant;
      if (held(detail.next, variant) > held(detail.prev, variant)) {
        intent = pendingOrder;
        pendingOrder = null;
      }
    }
    orderLines(intent);

    // The top: closed bag, end state; open bag, old -> new (or still waiting for the slide: from the start).
    const top = topEl();
    const heldRun = pendingRun && detail.open;
    if (detail.open) pendingRun = false;
    if (top) {
      if (!detail.open) {
        settleTop();
      } else if (heldRun) {
        // zao#617: the answer to the add that opened the bag: the one run, start of the line -> the new total.
        runTop(null, Math.max(0, openRunEndsAt - Date.now()), null);
      } else if (Date.now() < openRunEndsAt) {
        runTop(null, openRunEndsAt - Date.now(), null);
      } else {
        const oldSentence = previousPart('[data-bag-sentence]', detail.prevBody, detail.prevFigures);
        runTop(previousTop(detail.prev, detail.prevBody, detail.prevFigures), 0, oldSentence ? oldSentence.innerHTML : null);
      }
    }

    // A removed line leaves its Undo row where it was; any Undo row still running comes back after the render.
    if (pendingRemove) {
      const entry = pendingRemove;
      pendingRemove = null;
      if (held(detail.next, entry.variant) < held(detail.prev, entry.variant)) startUndo(entry);
    }
    undoRows.forEach((entry) => {
      if (entry.element && !entry.element.isConnected) {
        if (Date.now() >= entry.expires) {
          endUndo(entry, false);
        } else {
          placeUndoRow(entry.element, entry);
        }
      }
    });

    // A colour change cross-fades the photo (150 ms).
    if (pendingSwap && held(detail.next, pendingSwap.to) <= held(detail.prev, pendingSwap.to)) pendingSwap = null;
    if (pendingSwap) {
      const swap = pendingSwap;
      pendingSwap = null;
      const line = lineByVariant(swap.to);
      const photo = line && line.querySelector('[data-bag-photo]');
      if (swap.photo && photo && motion()) {
        swap.photo.classList.add('bag-line__photo--ghost');
        swap.photo.removeAttribute('data-bag-photo');
        swap.photo.setAttribute('aria-hidden', 'true');
        photo.appendChild(swap.photo);
        const out = animate(swap.photo, [{ opacity: 1 }, { opacity: 0 }], { duration: T.words(), easing: 'linear', fill: 'forwards' });
        done(out).then(() => swap.photo.remove());
      }
    }

    if (pendingCts && !pendingCts.variants.some((id) => held(detail.next, id) > held(detail.prev, id))) pendingCts = null;
    ctsAfterRender(detail.prevBody);
    if (focusAfter) restoreFocus();
  });

  document.addEventListener('sofsy:bag-closed', (event) => {
    const reason = (event.detail && event.detail.reason) || 'close';
    // silent: cart.js closing a bag she left for checkout, as the page goes (no tap of hers, nothing to count).
    const silent = !!(event.detail && event.detail.silent);
    closePanel(false);
    closeAsk();
    undoRows.slice().forEach((entry) => endUndo(entry, false));
    setHeader(false, true);
    const open = endCheckoutWatch();
    pendingRun = false;
    if (statusStays) say('');
    const failedOpen = failedTag();
    openAddFailed = false;
    if (silent) {
      setViewsWaiting = false;
      return;
    }
    if (setViewsWaiting) trackSetViews(); // closed before the add's answer: she still saw the bag
    const scroller = bag() && bag().querySelector('[data-bag-scroll]');
    const lines = bag() ? bag().querySelectorAll('[data-bag-line]').length : 0;
    // The event dictionary's words for how it closed: close (the X), scrim (the dim), escape, back, header.
    // zao#617: how long it was open, and whether Secure checkout was seen in that time.
    const closed = Object.assign({ how: CLOSE_HOW[reason] || reason }, failedOpen);
    if (open.ms !== null) closed.ms_open = open.ms;
    if (open.seen !== null) closed.checkout_seen = open.seen;
    track('bag_closed', closed);
    if (scroller && scroller.scrollHeight > scroller.clientHeight) {
      track('bag_scrolled', {
        depth: Math.round((100 * (scrollMax + scroller.clientHeight)) / scroller.scrollHeight),
        lines
      });
    }
  });

  document.addEventListener('sofsy:bag-escape', (event) => {
    if (openRow) {
      const { line, kind } = openRow;
      closePanel(true);
      const button = line.querySelector(`[data-bag-open="${kind}"]`);
      if (button) button.focus({ preventScroll: true });
      event.preventDefault();
    } else if (askLine) {
      closeAsk(true);
      event.preventDefault();
    }
  });

  /* =================================================================================================
   * Taps
   * ================================================================================================= */
  document.addEventListener('click', (event) => {
    const root = bag();
    const target = event.target instanceof Element ? event.target : null;
    if (!root || !target || !root.contains(target)) return;
    if (statusStays) say(''); // she has acted: the refused add's sentence goes
    const inBody = target.closest('[data-bag-body]');
    if ((busy || root.pendingAdd) && inBody) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const line = lineOf(target);
    const byKeyboard = event.detail === 0;

    // A tap outside the open row or the question closes it with no change (O1, RM3); a tap on another control
    // (another row, the stepper, an X, Complete the Set) then also does its own job, as the approved card does.
    if (openRow && !(line === openRow.line && target.closest(`[data-bag-panel="${openRow.kind}"], [data-bag-open="${openRow.kind}"]`))) {
      closePanel(true);
    }
    if (askLine && !(line === askLine && target.closest('[data-bag-ask], [data-bag-remove]'))) {
      closeAsk();
    }
    if (!line) {
      const add = target.closest('[data-bag-cts-add]');
      if (add) return ctsAdd(add, false);
      const link = target.closest('[data-cts-link="bag"]');
      if (link) return ctsAdd(link, true);
      const careAdd = target.closest('[data-bag-care-add]');
      if (careAdd) return ctsAdd(careAdd, false, 'care');
      const careLink = target.closest('[data-cts-link="bag-care"]');
      if (careLink) return ctsAdd(careLink, true, 'care');
      const ctsPhoto = target.closest('[data-bag-cts-photo]');
      if (ctsPhoto) {
        const card = ctsPhoto.closest('[data-bag-cts-card]');
        track('complete_set_photo_tapped', { shade: card ? card.dataset.shadeName : '', place: 'bag' });
        return;
      }
      const undoButton = target.closest('[data-bag-undo]');
      if (undoButton) {
        const entry = undoRows.find((row) => row.element === undoButton.closest('[data-bag-undo-row]'));
        undo(entry);
      }
      return;
    }

    const opener = target.closest('[data-bag-open]');
    if (opener) {
      const kind = opener.dataset.bagOpen;
      if (openRow && openRow.line === line && openRow.kind === kind) {
        closePanel(true);
      } else {
        openPanel(line, kind, byKeyboard);
      }
      return;
    }
    const shade = target.closest('[data-bag-shade]');
    if (shade) {
      if (shade.getAttribute('aria-disabled') === 'true') {
        const name = shade.dataset.shadeName;
        showNote(line, 'colour', fill(words().sold_out_note, { shade: name, size: line.dataset.sizeLabel }));
        track('bag_soldout_shade_tapped', { shade: name, size: line.dataset.sizeLabel });
        return;
      }
      return swapTo(line, 'colour', shade);
    }
    const pill = target.closest('[data-bag-size-variant]');
    if (pill) {
      if (pill.getAttribute('aria-disabled') === 'true') return; // a sold-out size does nothing (P30, SO2)
      return swapTo(line, 'size', pill);
    }
    const stepper = target.closest('[data-bag-step]');
    if (stepper) return step(line, Number(stepper.dataset.bagStep), stepper);
    if (target.closest('[data-bag-remove]')) return ask(line);
    if (target.closest('[data-bag-remove-keep]')) {
      track('bag_line_remove_kept', { variant: Number(line.dataset.variantId) || null });
      return closeAsk(true);
    }
    if (target.closest('[data-bag-remove-yes]')) return removeLine(line);
    const nameLink = target.closest('[data-bag-name-link]');
    if (nameLink) {
      // MC n29 (Ben, 3 Oct): the name opens the product page. Before she goes, note the bag as checkout does, so
      // Back lands on this page with the bag open at the same place (assets/cart.js noteLeaving). A new-tab tap
      // (Cmd, Ctrl, Shift, middle) leaves this page as it is.
      track('bag_photo_tapped', { place: 'line_name', product: Number(line.dataset.productId) || null, shade: line.dataset.shadeName });
      if (!(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0)) {
        const items = document.querySelector('cart-items.cart-wrapper');
        if (items && typeof items.noteLeaving === 'function') items.noteLeaving();
      }
      return;
    }
    if (target.closest('[data-bag-photo]')) {
      // LP0: the photo does nothing until #45; the tap is counted.
      track('bag_photo_tapped', { place: 'line', product: Number(line.dataset.productId) || null, shade: line.dataset.shadeName });
    }
  });

  // Arrow keys move between the circles or the pills of an open row (radio groups).
  document.addEventListener('keydown', (event) => {
    if (!/^(ArrowLeft|ArrowRight|ArrowUp|ArrowDown|Home|End)$/.test(event.key)) return;
    const target = event.target instanceof Element ? event.target : null;
    const group = target && target.closest('cart-items [role="radiogroup"]');
    if (!group) return;
    const radios = Array.from(group.querySelectorAll('[role="radio"]'));
    const index = radios.indexOf(target.closest('[role="radio"]'));
    if (index < 0) return;
    event.preventDefault();
    let next = index;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = radios.length - 1;
    else next = (index + (/Right|Down/.test(event.key) ? 1 : -1) + radios.length) % radios.length;
    radios.forEach((radio, i) => radio.setAttribute('tabindex', i === next ? '0' : '-1'));
    radios[next].focus();
  });

  /* =================================================================================================
   * Did she see Secure checkout (zao#617): bag.checkout.impression, and bag_closed's ms_open / checkout_seen
   * ================================================================================================= */
  const IMPRESSION_RATIO = 0.5; // at least half of the button inside the viewport
  const clock = () => {
    try {
      if (window.performance && typeof window.performance.now === 'function') return window.performance.now();
    } catch (_) {
      /* fall through */
    }
    return Date.now();
  };
  let openedAt = null; // clock() at the open
  let checkoutSeen = null; // null: not watched (no IntersectionObserver or no button); else seen in this open
  let checkoutObserver = null;

  function stopCheckoutObserver() {
    if (!checkoutObserver) return;
    try {
      checkoutObserver.disconnect();
    } catch (_) {
      /* already gone */
    }
    checkoutObserver = null;
  }

  // On every open: the clock starts and the button is watched until it has been seen once. startAt: the open's
  // real time (clock()), for an open that happened before this file loaded (catchUpOpen).
  function watchCheckout(startAt) {
    stopCheckoutObserver();
    openedAt = typeof startAt === 'number' && Number.isFinite(startAt) ? Math.min(startAt, clock()) : clock();
    checkoutSeen = null;
    const root = bag();
    const button = root && root.querySelector('#checkout');
    if (!button || typeof window.IntersectionObserver !== 'function') return;
    try {
      checkoutSeen = false;
      checkoutObserver = new window.IntersectionObserver(
        (entries) => {
          // Only while the bag is open: a callback that lands after the close (or for a closed bag) is no impression.
          if (checkoutSeen !== false || openedAt === null || !isOpen()) return;
          const inView = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= IMPRESSION_RATIO);
          if (!inView) return;
          checkoutSeen = true;
          stopCheckoutObserver();
          track('bag.checkout.impression', {
            ms_since_open: Math.max(0, Math.round(clock() - openedAt)),
            scrolled: scrollMax > 0
          });
        },
        { threshold: [0, IMPRESSION_RATIO, 1] }
      );
      checkoutObserver.observe(button);
    } catch (_) {
      checkoutObserver = null;
      checkoutSeen = null;
    }
  }

  // On close: {ms, seen} for bag_closed (null where unknown), and the watch ends.
  function endCheckoutWatch() {
    stopCheckoutObserver();
    const out = {
      ms: openedAt === null ? null : Math.max(0, Math.round(clock() - openedAt)),
      seen: checkoutSeen
    };
    openedAt = null;
    checkoutSeen = null;
    return out;
  }

  /* =================================================================================================
   * Scrolling: how deep she reads (bag_scrolled) and SC1's returning header (off at launch)
   * ================================================================================================= */
  let scrollMax = 0;
  let lastTop = 0;
  let travel = 0;
  let headerOn = false;
  function setHeader(on, instant) {
    const head = bag() && bag().querySelector('[data-bag-head]');
    if (!head || headerOn === on) return;
    headerOn = on;
    if (on) {
      head.classList.add('is-returning');
      if (!instant && motion()) animate(head, [{ transform: 'translateY(-100%)' }, { transform: 'none' }], { duration: 300, easing: 'cubic-bezier(0.2, 0, 0, 1)' });
      track('bag_header_revealed', {});
    } else if (instant || !motion()) {
      head.classList.remove('is-returning');
    } else {
      const out = animate(head, [{ transform: 'none' }, { transform: 'translateY(-100%)' }], {
        duration: 250,
        easing: 'cubic-bezier(0.2, 0, 0, 1)',
        fill: 'forwards'
      });
      done(out).then(() => {
        if (!headerOn) head.classList.remove('is-returning');
        head.getAnimations().forEach((a) => a.cancel());
      });
    }
  }
  document.addEventListener(
    'scroll',
    (event) => {
      const scroller = event.target;
      if (!(scroller instanceof Element) || !scroller.matches('[data-bag-scroll]')) return;
      const top = scroller.scrollTop;
      scrollMax = Math.max(scrollMax, top);
      const returning = bag() && bag().dataset.returningHeader === 'true';
      if (!returning) return;
      const delta = top - lastTop;
      lastTop = top;
      if (top <= 0) {
        travel = 0;
        setHeader(false, true);
        return;
      }
      travel = Math.sign(delta) === Math.sign(travel) ? travel + delta : delta;
      if (!headerOn && top > 50 && travel <= -10) setHeader(true, false);
      else if (headerOn && travel >= 10) setHeader(false, false);
    },
    true
  );
  // SC1: a keyboard reaching the X while the header is scrolled away brings it back.
  document.addEventListener('focusin', (event) => {
    const root = bag();
    if (!root || root.dataset.returningHeader !== 'true') return;
    if (event.target instanceof Element && event.target.closest('[data-bag-close]')) {
      const scroller = root.querySelector('[data-bag-scroll]');
      if (scroller && scroller.scrollTop > 50) setHeader(true, false);
    }
  });

  /* ---------- first paint: the order she last saw, the money and the amount's place, before the bag is ever opened */
  const firstPaint = () => {
    const root = bag();
    if (!root) return;
    catchUpOpen();
    orderLines(null);
    paintMoney(root.querySelector('[data-bag-body]'));
    paintMoney(root.querySelector('[data-bag-figures]')); // B5
    const top = topEl();
    if (top) placeAmount(top, topValues(top).share);
  };
  catchUpOpen();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', firstPaint);
  else firstPaint();
  window.addEventListener('load', firstPaint);

  window.SofsyBag = Object.freeze({
    runTop: () => runTop(null, 0, null),
    paint: firstPaint,
    say // the failed-add line, for cart.js (zao#617: a refused add that had already opened the bag)
  });
})();
